/**
 * The "Check out" thread CTA — stop it bouncing the visitor to the homepage,
 * and give it the same honest answer every one of its siblings already gets.
 *
 * THE BUG TYLER REPORTED (2026-08-27, signed in on staging): tapping "Check
 * out" on a deal does not open checkout — it lands you back on the home feed
 * with no explanation.
 *
 * THE CAUSE IS OUR OWN GUARD, and the chain is worth stating because it is not
 * obvious from either end:
 *   1. `[data-acceptoffer]` is in writeGuard's GUARDED_WRITES — deliberately,
 *      because his handler fabricates an acceptance and invents the fees
 *      (barterTax 12, shipping 14) with no server call.
 *   2. That handler is the only place in APP.JS that assigns `t.fees` —
 *      `.fees =` appears there exactly once.
 *   3. `route()` resolves `#/checkout/<id>` only for a thread that HAS fees:
 *      `THREADS.find((x) => x.id === m[1] && x.fees)` (app.js:3714). A miss
 *      falls through every remaining branch to `{ view: "home" }`.
 *   ⇒ accept is suppressed, fees is never set, the route never resolves, and
 *      the visitor lands on the home feed with no explanation.
 * So the bounce is a CASCADE of the guard, not an independent defect. The
 * guard is right; the unexplained bounce is the part that is wrong.
 *
 * 🔴 STEP 2 IS ABOUT APP.JS ONLY, AND THE DISTINCTION MATTERS — an earlier
 * draft of this docblock said "the whole of app.js" and reasoned from it as
 * though nothing at all could set fees. floorTransport's
 * `__gbzInstallThreadFees` assigns `THREADS[i].fees` too, from
 * GET /api/offers/<id>/trade-checkout-summary mapped through `mapThreadFees`,
 * and its own docblock says setting it truthy "is ALSO what makes
 * #/checkout/<id> resolvable". It fires when a visitor taps "Tap for summary ›"
 * on an accepted deal. The server, for its part, only emits the plain gold
 * "Check out" CTA for a deal it will NOT refuse — `payBlockedSide` is derived
 * through lib/checkoutAddress, the same module trade-checkout-summary refuses
 * with (deal-threads.ts:609). So there is a reachable state in which this CTA
 * would open a checkout screen carrying six canonical fee lines rather than
 * invented ones, and this guard suppresses that state too.
 *
 * ✅ SUPPRESSING IT ANYWAY IS THE DECISION (Tyler, 2026-08-27), not an
 * oversight, and the reason is what waits on the other side of the door.
 * `[data-completecheckout]` — the only action that screen exists to offer — is
 * itself in GUARDED_WRITES, and a guarded write is a console.warn and nothing
 * else: silent to the visitor. Walking someone into an accurate breakdown
 * whose one button then dies without a word is worse than telling them at the
 * door that it opens in production. The two are ONE decision: when Blake wires
 * the real write (option A on card 1217799695855666) and
 * `[data-completecheckout]` leaves GUARDED_WRITES, this guard should leave
 * with it. `checkoutCtaGuard.test.ts` pins both halves — a control proving the
 * fees-installed route really does resolve, and its sibling proving we
 * intercept it regardless.
 *
 * WHY NOT JUST ADD `[data-threadcta]` TO GUARDED_WRITES. Two reasons, and the
 * first one alone settles it:
 *   · It would suppress ALL of them. One handler serves every thread CTA
 *     (app.js:4992), and "View QR drop-off label" opens the tracking modal
 *     while every other value already toasts honestly. Guarding the selector
 *     would break three working controls to fix one broken one.
 *   · GUARDED_WRITES is also the expensive list to grow: demoWriteGuard-
 *     Derivation's final test requires every entry to be a string-exact member
 *     of the receivers its enumerator finds in app.js, and demoContract
 *     auto-derives an anchor per entry against a measured `ANCHORS.length`.
 *     A separate interceptor costs neither.
 *
 * WHY MATCHING THE LITERAL "Check out" IS CORRECT HERE, despite the usual rule
 * against keying on user-visible copy. We match the SAME string his own
 * handler branches on:
 *     if (el.dataset.threadcta === "Check out") { location.hash = "#/checkout/" + dealsSel; return; }
 * That puts us in lockstep rather than in a race. If the label is ever
 * reworded — and it is server-driven, `t.cta` comes from dealState.cta() via
 * floorTransport — then HIS navigation branch stops matching too, the CTA
 * falls through to his own toast, and both the bug and this fix become
 * irrelevant together. A cleverer match would be MORE fragile, not less,
 * because it could catch a CTA his branch no longer navigates.
 *
 * WE INVENT NO COPY. writeGuard's docblock rules that user-facing wording for
 * a suppressed control is a product decision and warns against inventing it in
 * our layer. We do not have to: his handler's own fallback for every
 * non-navigating CTA is `toast(el.dataset.threadcta + " · opens in
 * production")`. We apply that same fallback, built from the element's own
 * value, to the one CTA that escapes it. The message a visitor sees is
 * therefore Blake's, not ours.
 *
 * 🪤 `window.toast` IS reachable, and the reason is a subtlety worth keeping.
 * `toast` is a top-level `function` DECLARATION in public/web/data.js:841, and
 * in a classic script those become properties of the global object — so
 * `window.toast` exists. Its siblings `$`, `esc`, `money` and the data arrays
 * are `const`, which create global LEXICAL bindings that are NOT on window,
 * which is exactly why WebInstall has to inject a bridge script to reach
 * `LISTINGS` by name. Do not copy this pattern to those.
 *
 * 🪤 LIFECYCLE, NOT SCOPE, is the real hazard: `toast()` does
 * `$("#toast").textContent = msg` with no null check, and #toast only exists
 * after app.js's render() has painted. The default notifier below checks for
 * the element first, so a click that somehow arrives before the first render
 * degrades to doing nothing rather than throwing inside a capture listener.
 */

/** His selector, and the one value of it that navigates (app.js:4992-4994). */
export const THREAD_CTA_SELECTOR = "[data-threadcta]";
export const CHECKOUT_CTA_VALUE = "Check out";

/** Verbatim from his own fallback for every other CTA (app.js:4996). */
export const OPENS_IN_PRODUCTION_SUFFIX = " · opens in production";

export function installCheckoutCtaGuard(
  win: Window,
  /** Seam for tests, and the place the #toast lifecycle guard lives. */
  notify: (message: string) => void = (message) => {
    if (!win.document.getElementById("toast")) return;
    (win as unknown as { toast?: (m: string) => void }).toast?.(message);
  },
): () => void {
  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;
    const el = target.closest(THREAD_CTA_SELECTOR) as HTMLElement | null;
    if (!el) return;
    const value = el.dataset.threadcta;
    // Every other value either opens the tracking modal or already toasts —
    // both correct, both left alone.
    if (value !== CHECKOUT_CTA_VALUE) return;

    event.stopPropagation();
    event.preventDefault();
    notify(value + OPENS_IN_PRODUCTION_SUFFIX);
  };

  // Capture phase on the document, same as writeGuard and linkFixups: his
  // listener is bound on the element in the target phase, so stopping the
  // descent is what prevents it running. stopPropagation, never
  // stopImmediatePropagation — we own other document capture listeners.
  win.document.addEventListener("click", onClick, true);
  return () => win.document.removeEventListener("click", onClick, true);
}
